// valuePass/admin/src/components/layout/AppShell.tsx
//
// Root layout shell for the ValuePass admin dashboard.
//
// ── Structure ─────────────────────────────────────────────────────────────────
//
//   AppShell
//   ├─ Sidebar          (left, fixed, collapsible)
//   └─ Main area
//      ├─ TopBar        (page title, breadcrumb, mobile menu toggle)
//      └─ <children />  (page content — routed via React Router Outlet)
//
// ── Responsive behaviour ──────────────────────────────────────────────────────
//
//   Desktop (≥ 1024px)
//     Sidebar visible, toggle button collapses it to a 64px icon rail.
//     Main area fills remaining width with smooth transition.
//
//   Tablet (768–1023px)
//     Sidebar hidden off-screen by default. Menu button slides it in as an
//     overlay with a backdrop. Collapses back on backdrop click or nav.
//
//   Mobile (< 768px)
//     Same as tablet.
//
// ── Context ───────────────────────────────────────────────────────────────────
//
//   CampaignContext  — active campaign ID + setter. Every page reads the
//                      active campaign from this context rather than URL params
//                      so that navigating between pages keeps the selection.
//
//   AuthContext      — signed-in Firebase Auth user. AppShell redirects to
//                      /login when the user is not authenticated.
//
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  onAuthStateChanged,
  type User,
} from "firebase/auth";
import { getFirebaseAuth } from "@/lib/firebase";
import { Sidebar } from "./Sidebar";
import { campaignsApi } from "@/api/client";
import type { Campaign } from "@/types";

// ── Campaign context ──────────────────────────────────────────────────────────

interface CampaignContextValue {
  /** The Firestore document ID of the active campaign. null = none selected. */
  activeCampaignId: string | null;
  /** The full active Campaign document. null until loaded. */
  activeCampaign:   Campaign | null;
  /** All campaigns available to the current user. */
  campaigns:        Campaign[];
  /** Change the active campaign. */
  setActiveCampaignId: (id: string) => void;
  /** True while the initial campaign list is loading. */
  loading: boolean;
}

const CampaignContext = createContext<CampaignContextValue>({
  activeCampaignId:    null,
  activeCampaign:      null,
  campaigns:           [],
  setActiveCampaignId: () => undefined,
  loading:             true,
});

/**
 * Hook to access the active campaign context from any component inside AppShell.
 *
 * @example
 * const { activeCampaignId, activeCampaign, campaigns } = useCampaignContext();
 */
export function useCampaignContext(): CampaignContextValue {
  return useContext(CampaignContext);
}

// ── Auth context ──────────────────────────────────────────────────────────────

interface AuthContextValue {
  user:        User | null;
  authLoading: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  user:        null,
  authLoading: true,
});

/**
 * Hook to access the signed-in Firebase Auth user from any component inside AppShell.
 *
 * @example
 * const { user, authLoading } = useAuthContext();
 */
export function useAuthContext(): AuthContextValue {
  return useContext(AuthContext);
}

// ── Breakpoint constant ───────────────────────────────────────────────────────

const DESKTOP_BREAKPOINT = 1024; // px — matches Tailwind's `lg` breakpoint

// ── Route → breadcrumb label map ─────────────────────────────────────────────

const ROUTE_LABELS: Record<string, string> = {
  "/":             "Dashboard",
  "/campaigns":    "Campaigns",
  "/vouchers":     "Vouchers",
  "/beneficiaries":"Beneficiaries",
  "/analytics":    "Analytics",
  "/merchants":    "Merchants",
  "/settings":     "Settings",
};

function getPageTitle(pathname: string): string {
  // Exact match
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  // Prefix match for nested routes (e.g., /campaigns/camp_abc → "Campaigns")
  const prefix = Object.keys(ROUTE_LABELS).find(
    (k) => k !== "/" && pathname.startsWith(k),
  );
  return prefix ? ROUTE_LABELS[prefix] : "ValuePass Admin";
}

// ── TopBar ────────────────────────────────────────────────────────────────────

const TopBar: React.FC<{
  pageTitle:      string;
  onMenuToggle:   () => void;
  isMobile:       boolean;
  sidebarVisible: boolean;
}> = ({ pageTitle, onMenuToggle, isMobile }) => (
  <header className="flex-shrink-0 h-14 bg-white border-b border-gray-200 flex items-center gap-3 px-4 shadow-sm z-10">
    {/* Mobile hamburger */}
    {isMobile && (
      <button
        onClick={onMenuToggle}
        className="p-1.5 -ml-1 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        aria-label="Toggle navigation menu"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
          <path d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
        </svg>
      </button>
    )}

    {/* Page title */}
    <h1 className="text-base font-semibold text-gray-800 truncate flex-1">
      {pageTitle}
    </h1>

    {/* Right-side actions placeholder */}
    <div className="flex items-center gap-2">
      {/* Notification bell — placeholder for future notifications feature */}
      <button
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        aria-label="Notifications (coming soon)"
        title="Notifications (coming soon)"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
        </svg>
      </button>

      {/* POS simulator link */}
      <a
        href="/pos/index.html?v=3"
        target="_blank"
        rel="noopener noreferrer"
        className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-green border border-brand-green/30 rounded-lg hover:bg-brand-green hover:text-white transition-colors"
        title="Open POS simulator in a new tab"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
        </svg>
        POS Simulator
      </a>
    </div>
  </header>
);

// ── Mobile backdrop ───────────────────────────────────────────────────────────

const MobileBackdrop: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div
    className="fixed inset-0 bg-black/50 z-30 lg:hidden"
    onClick={onClick}
    aria-hidden="true"
  />
);

// ── Loading screen ────────────────────────────────────────────────────────────

const FullPageLoader: React.FC<{ message?: string }> = ({
  message = "Loading…",
}) => (
  <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3">
    <div className="w-10 h-10 rounded-full border-4 border-brand-green border-t-transparent animate-spin" />
    <p className="text-sm text-gray-500">{message}</p>
  </div>
);

// ── Desktop collapse toggle ───────────────────────────────────────────────────

const CollapseToggle: React.FC<{
  collapsed: boolean;
  onToggle:  () => void;
}> = ({ collapsed, onToggle }) => (
  <button
    onClick={onToggle}
    className="hidden lg:flex absolute -right-3 top-6 w-6 h-6 bg-white border border-gray-200 rounded-full items-center justify-center shadow-sm text-gray-500 hover:text-gray-700 hover:shadow transition-shadow z-20"
    aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
    title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
  >
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className={`w-3.5 h-3.5 transition-transform duration-200 ${
        collapsed ? "rotate-0" : "rotate-180"
      }`}
    >
      <path
        fillRule="evenodd"
        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  </button>
);

// ── AppShell ──────────────────────────────────────────────────────────────────

export interface AppShellProps {
  /** Rendered inside the main content area. Typically <Outlet /> from React Router. */
  children?: ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Responsive state ───────────────────────────────────────────────────────

  const [isMobile,        setIsMobile]        = useState(() => window.innerWidth < DESKTOP_BREAKPOINT);
  const [sidebarOpen,     setSidebarOpen]      = useState(false);  // mobile overlay
  const [sidebarCollapsed,setSidebarCollapsed] = useState(false);  // desktop rail

  useEffect(() => {
    const handler = () => {
      const mobile = window.innerWidth < DESKTOP_BREAKPOINT;
      setIsMobile(mobile);
      // When resizing to desktop, close the mobile overlay
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", handler, { passive: true });
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const toggleMobileSidebar  = useCallback(() => setSidebarOpen((v)  => !v),  []);
  const toggleDesktopCollapse = useCallback(() => setSidebarCollapsed((v) => !v), []);

  // ── Auth state ─────────────────────────────────────────────────────────────

  const [user,        setUser]        = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    let auth: ReturnType<typeof getFirebaseAuth>;
    try {
      auth = getFirebaseAuth();
    } catch {
      // Firebase not yet initialised — handled by main.tsx
      setAuthLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);

      // Redirect unauthenticated users to the login page
      if (!u && location.pathname !== "/login") {
        navigate("/login", { replace: true, state: { from: location.pathname } });
      }
    });

    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Campaign state ─────────────────────────────────────────────────────────

  const [campaigns,         setCampaigns]         = useState<Campaign[]>([]);
  const [activeCampaignId,  setActiveCampaignId]  = useState<string | null>(null);
  const [campaignsLoading,  setCampaignsLoading]  = useState(true);

  useEffect(() => {
    if (!user) return; // Don't load campaigns until authenticated

    setCampaignsLoading(true);

    campaignsApi
      .list({ limit: 50 })
      .then(({ campaigns: list }) => {
        setCampaigns(list);
        // Auto-select the first ACTIVE campaign, or just the first one
        if (list.length > 0) {
          const first =
            list.find((c) => c.status === "ACTIVE") ?? list[0];
          setActiveCampaignId(first.campaignId);
        }
      })
      .catch((err) => {
        console.error("[AppShell] Failed to load campaigns:", err);
      })
      .finally(() => {
        setCampaignsLoading(false);
      });
  }, [user]);

  const activeCampaign = useMemo(
    () => campaigns.find((c) => c.campaignId === activeCampaignId) ?? null,
    [campaigns, activeCampaignId],
  );

  // ── Context values ─────────────────────────────────────────────────────────

  const campaignContextValue = useMemo<CampaignContextValue>(
    () => ({
      activeCampaignId,
      activeCampaign,
      campaigns,
      setActiveCampaignId,
      loading: campaignsLoading,
    }),
    [activeCampaignId, activeCampaign, campaigns, campaignsLoading],
  );

  const authContextValue = useMemo<AuthContextValue>(
    () => ({ user, authLoading }),
    [user, authLoading],
  );

  // ── Derived sidebar props ──────────────────────────────────────────────────

  const sidebarCampaigns = useMemo(
    () =>
      campaigns.map((c) => ({
        campaignId: c.campaignId,
        name:       c.name,
        status:     c.status,
      })),
    [campaigns],
  );

  const sidebarUser = user
    ? {
        displayName: user.displayName,
        email:       user.email,
        photoURL:    user.photoURL,
      }
    : null;

  // ── Guards ─────────────────────────────────────────────────────────────────

  if (authLoading) {
    return <FullPageLoader message="Authenticating…" />;
  }

  // If we're on /login, render nothing (LoginPage handles its own layout)
  if (location.pathname === "/login") {
    return <>{children ?? <Outlet />}</>;
  }

  // If user is null after auth loaded → the useEffect above redirects to /login
  if (!user) return null;

  // ── Render ─────────────────────────────────────────────────────────────────

  const pageTitle = getPageTitle(location.pathname);

  /*
   * Layout grid:
   *
   *  ┌──────────────┬────────────────────────────────────┐
   *  │              │  TopBar                            │
   *  │   Sidebar    ├────────────────────────────────────┤
   *  │              │  <page content>                    │
   *  │              │                                    │
   *  └──────────────┴────────────────────────────────────┘
   *
   * On mobile the sidebar sits off-screen and overlays on toggle.
   */

  return (
    <AuthContext.Provider value={authContextValue}>
      <CampaignContext.Provider value={campaignContextValue}>
        <div className="flex h-screen overflow-hidden bg-gray-50">

          {/* ── Mobile backdrop ────────────────────────────────────────── */}
          {isMobile && sidebarOpen && (
            <MobileBackdrop onClick={toggleMobileSidebar} />
          )}

          {/* ── Sidebar ────────────────────────────────────────────────── */}
          <div
            className={[
              // Desktop: always in flow, width transitions
              "relative hidden lg:flex",
              sidebarCollapsed ? "lg:w-16" : "lg:w-56",
              "transition-[width] duration-200 ease-in-out",
            ].join(" ")}
          >
            {/* Desktop collapse toggle button */}
            <CollapseToggle
              collapsed={sidebarCollapsed}
              onToggle={toggleDesktopCollapse}
            />

            <Sidebar
              collapsed={sidebarCollapsed}
              campaigns={sidebarCampaigns}
              activeCampaignId={activeCampaignId}
              onCampaignChange={(id) => setActiveCampaignId(id || null)}
              user={sidebarUser}
            />
          </div>

          {/* ── Mobile sidebar (overlay) ────────────────────────────────── */}
          <div
            className={[
              "fixed inset-y-0 left-0 z-40 flex lg:hidden",
              "transition-transform duration-200 ease-in-out",
              sidebarOpen ? "translate-x-0" : "-translate-x-full",
            ].join(" ")}
            aria-hidden={!sidebarOpen}
          >
            <Sidebar
              collapsed={false}
              campaigns={sidebarCampaigns}
              activeCampaignId={activeCampaignId}
              onCampaignChange={(id) => {
                setActiveCampaignId(id || null);
                setSidebarOpen(false);
              }}
              user={sidebarUser}
            />
          </div>

          {/* ── Main content area ───────────────────────────────────────── */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

            {/* TopBar */}
            <TopBar
              pageTitle={pageTitle}
              onMenuToggle={toggleMobileSidebar}
              isMobile={isMobile}
              sidebarVisible={sidebarOpen}
            />

            {/* Page content */}
            <main
              id="main-content"
              className="flex-1 overflow-y-auto"
              aria-label="Page content"
            >
              <div className="max-w-screen-2xl mx-auto px-4 py-6 sm:px-6">
                {children ?? <Outlet />}
              </div>
            </main>
          </div>
        </div>
      </CampaignContext.Provider>
    </AuthContext.Provider>
  );
};

export default AppShell;
